import { StepperSelectionEvent } from "@angular/cdk/stepper";
import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { LStep } from "core/dto";
import { subjectFromObs } from 'core/rx-js';
import { Subject, combineLatest, filter, map, takeUntil } from "rxjs";
import { DsgDeepTranslationKey } from "../../pipes/translate.pipe";
import { ProcessStepperService } from "./process-stepper.service";
import { RemoteActivityService } from "./remotes/remote-activity.service";
import { RemotePdfDocumentService } from "./remotes/remote-pdf-document.service";
import { RemoteProcessService } from "./remotes/remote-process.service";
import { RemoteSplugParametersConfigurationService } from "./remotes/remote-splug-parameters-configuration.service";
import { RemoteTaskService } from "./remotes/remote-task.service";
import { SignProcessWizardService } from "./sign-process-wizard.service";
import { DocumentCollectionService } from "./steps/1-document-upload-step/document-collection/document-collection.service";
import { DocumentUploadStepService } from "./steps/1-document-upload-step/document-upload-step.service";
import { ActorSplugInfoDialogService } from "./steps/2-select-activities-step/activity-collection/activity-item/actor-splug-info-dialog.service";
import { SelectActivitiesStepService } from "./steps/2-select-activities-step/select-activities-step.service";
import { SelectTasksStepService } from "./steps/3-select-tasks-step/select-tasks-step.service";
import { ReviewStepService } from "./steps/4-review-step/review-step.service";

@Component({
  selector: 'ss-sign-process-wizard',
  templateUrl: './sign-process-wizard.component.html',
  styleUrls: ['./sign-process-wizard.component.scss'],
  providers: [
    SignProcessWizardService,
    DocumentUploadStepService,
    DocumentCollectionService,
    ProcessStepperService,
    SelectActivitiesStepService,
    SelectTasksStepService,
    ReviewStepService,
    RemoteProcessService,
    RemoteActivityService,
    RemotePdfDocumentService,
    RemoteTaskService,
    RemoteSplugParametersConfigurationService,
    ActorSplugInfoDialogService,

  ],
  host: {
    class: 'padding-standard full-stretch display-block'
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignProcessWizardComponent implements OnInit {

  private readonly _destroy = new Subject<void>();

  readonly documentUploadStepValidObs = this._documentUploadStepService.stepValidObs;
  readonly screenSizeSubject = this._signProcessWizardService.screenSizeSubject;

  readonly processIdSubject = this._signProcessWizardService.processIdSubject;
  readonly selectActivitesStepValidObs = this._selectSignersStepService.stepValidObs;
  readonly selectTasksStepValidObs = this._selectTasksStepService.stepValidObs

  readonly triedNextSubject = this._processSteperService.triedNextSubject;
  readonly stepIdxObs = this._processSteperService.stepIdxObs;
  readonly currentStepSubject = this._processSteperService.currentStepSubject;

  readonly stepValidObs = combineLatest([
    this.documentUploadStepValidObs,
    this.selectActivitesStepValidObs,
    this.selectTasksStepValidObs
  ])
    .pipe(
      map(([
        documentUploadStepValid,
        selectSignersStepValid,
        selectTasksStepValid,
      ]): { [step in LStep]?: boolean } => ({
        documentUpload: documentUploadStepValid,
        selectSigners: selectSignersStepValid,
        setSignatures: selectTasksStepValid,
        review: true
      })
      )
    )

  readonly finishDisplayObs = this._signProcessWizardService.finishDisplayObs
  readonly stepsValidSubject = subjectFromObs<{ [KStep in LStep]?: boolean }>({ observable: this.stepValidObs, until: this._destroy, initialValue: {} })
  get stepsValid() {
    return this.stepsValidSubject.value;
  }

  @ViewChild('footer', { read: ElementRef })
  private _footer: ElementRef<HTMLElement>


  constructor(
    private readonly _documentUploadStepService: DocumentUploadStepService,
    private readonly _signProcessWizardService: SignProcessWizardService,
    private readonly _selectSignersStepService: SelectActivitiesStepService,
    private readonly _selectTasksStepService: SelectTasksStepService,
    private readonly _translateService: TranslateService,
    private readonly _processSteperService: ProcessStepperService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _elementRef: ElementRef<HTMLElement>
  ) {

    this._signProcessWizardService.processStatus = this._activatedRoute.snapshot.queryParams.processStatus;

    this._activatedRoute.params
      .pipe(
        map(params => params.processId),
        filter(Boolean),
        takeUntil(this._destroy),
      )
      .subscribe((processId) => {
        this.processIdSubject.next(processId);
      })
  }

  ngOnInit(): void {

    // this._signProcessWizardService.initProcess();

  }

  ngAfterViewInit() {

    const footer = this._footer.nativeElement;
    const header = this._elementRef.nativeElement.querySelector('.mat-horizontal-stepper-header-container');
    this._signProcessWizardService.footer = footer;
    this._signProcessWizardService.header = header as HTMLElement;
    this._signProcessWizardService.stepper = this._elementRef.nativeElement.children[0] as HTMLElement;
  }

  translate(translationKey: DsgDeepTranslationKey) {
    return this._translateService.get(translationKey);
  }

  tryNext() {
    const currentStep = this.currentStepSubject.value;

    const newTriedToNext = { ...this.triedNextSubject.value };
    newTriedToNext[currentStep] = true;

    this.triedNextSubject.next(newTriedToNext);
    const isValid = (this.stepsValid as any)[currentStep];

    if (isValid) {
      this._processSteperService.nextStep();
    }

  }

  stepChange(ev: StepperSelectionEvent) {

    const { selectedIndex } = ev;
    this._processSteperService.selectStep(selectedIndex);
  }

  prev() {

    this._processSteperService.prevStep();
  }

  finishProcess() {
    this._signProcessWizardService.finishProcess();
  }

  back() {

    this._signProcessWizardService.back();

  }

}
